import { api } from "@repo/convex/api";
import type { Doc, Id } from "@repo/convex/dataModel";
import type { OrgRole } from "@repo/shared";
import { useNavigate } from "@tanstack/react-router";
import { useConvex, useConvexAuth } from "convex/react";
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useObservedAction } from "@/hooks/use-observed-convex";
import { useQueryWithPlatformRetry } from "@/hooks/use-query-with-platform-retry";
import { breadcrumb, captureError } from "@/lib/observability";

/**
 * An org the signed-in user belongs to, carrying their role in it.
 *
 * The role is the shared `OrgRole` union rather than a bare string: every consumer compares it
 * against a role literal, and a widened type let a typo through silently.
 */
type OrganizationWithRole = Doc<"orgs"> & { role: OrgRole };
type OrganizationAccessState = {
  userStatus: "missing" | "present";
  organizations: OrganizationWithRole[];
};

/** Options accepted by {@link OrganizationContextValue.switchOrganization}. */
export interface SwitchOrganizationOptions {
  /**
   * Keep the current workspace URL instead of landing on the projects home.
   * The org-aware project-not-found page uses it so "Switch to {org}" resolves the same
   * deep link in the newly selected organization. Defaults to `false` (dropdown behavior).
   */
  keepCurrentLocation?: boolean;
}

/**
 * The most recent `switchOrganization` call, kept as plain state.
 *
 * It is a *record*, not an intent to be consumed: nothing clears it, so it never depends on
 * who reads it first or how often React re-runs an effect. Consumers must therefore match on
 * `orgId` before acting on `keepCurrentLocation` — the request describes one specific switch,
 * and a later switch (e.g. from the org dropdown) overwrites it with its own flags.
 */
export interface OrganizationSwitchRequest {
  orgId: Id<"orgs">;
  keepCurrentLocation: boolean;
}

interface OrganizationContextValue {
  organizationId: Id<"orgs"> | null;
  organization: OrganizationWithRole | null;
  /** All organizations the user belongs to (undefined while loading) */
  allOrganizations: OrganizationWithRole[] | undefined;
  /** Switch to a different organization by ID */
  switchOrganization: (orgId: Id<"orgs">, options?: SwitchOrganizationOptions) => void;
  /** Last switch requested through {@link OrganizationContextValue.switchOrganization}. */
  organizationSwitchRequest: OrganizationSwitchRequest | null;
  isLoading: boolean;
}

const STORAGE_KEY = "df-selected-org";

const OrganizationContext = createContext<OrganizationContextValue | null>(null);

function getStoredOrgId(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function storeOrgId(orgId: string) {
  try {
    localStorage.setItem(STORAGE_KEY, orgId);
  } catch {
    // localStorage unavailable
  }
}

export function OrganizationProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useConvexAuth();
  const convex = useConvex();
  const navigate = useNavigate();

  // Track the user's explicit org selection (null = use default/stored)
  const [selectedOrgId, setSelectedOrgId] = useState<Id<"orgs"> | null>(null);
  const [hasInitializedSelection, setHasInitializedSelection] = useState(false);
  const initializedFromStorage = useRef(false);

  // Every authenticated route renders under this provider, so a platform blip on this one
  // query would otherwise replace the whole app with the root error fallback.
  const organizationAccess = useQueryWithPlatformRetry(
    api.organizations.getMyOrganizationAccessState,
    isAuthenticated ? {} : "skip"
  );
  const [postBootstrapAccess, setPostBootstrapAccess] = useState<OrganizationAccessState | null>(
    null
  );
  const organizations = postBootstrapAccess?.organizations.length
    ? postBootstrapAccess.organizations
    : organizationAccess?.organizations;

  const ensureUser = useObservedAction(api.users.ensureUser, {
    feature: "organizations",
    operation: "ensure-user-organization-access",
    includeRequestId: true,
    failureOutcome: "best_effort_failure",
  });
  const bootstrapAttempted = useRef(false);
  const [isBootstrapping, setIsBootstrapping] = useState(false);
  const redirected = useRef(false);

  // Bootstrap user + organization access when the app shell observes either a
  // missing user row or a present user with no memberships. This uses the full
  // provisioning path; org-only sync assumes the user already exists.
  useEffect(() => {
    if (bootstrapAttempted.current) return;

    // Wait for query to complete
    if (!isAuthenticated || organizationAccess === undefined) return;

    const shouldBootstrap =
      organizationAccess.userStatus === "missing" || organizationAccess.organizations.length === 0;

    if (shouldBootstrap) {
      bootstrapAttempted.current = true;
      setPostBootstrapAccess(null);
      setIsBootstrapping(true);

      const runBootstrap = async () => {
        try {
          breadcrumb(
            "organization.bootstrap.start",
            { userStatus: organizationAccess.userStatus },
            { category: "user" }
          );
          await ensureUser({});
          const latestAccess = await convex.query(
            api.organizations.getMyOrganizationAccessState,
            {}
          );
          setPostBootstrapAccess(latestAccess);
          breadcrumb(
            "organization.bootstrap.success",
            {
              userStatus: organizationAccess.userStatus,
              latestUserStatus: latestAccess.userStatus,
              latestOrganizationCount: latestAccess.organizations.length,
            },
            { category: "user" }
          );
        } catch (error) {
          captureError(error, {
            feature: "organizations",
            operation: "ensure-user-organization-access",
            extra: {
              userStatus: organizationAccess.userStatus,
              organizationCount: organizationAccess.organizations.length,
            },
          });
          // Ignore and continue; this path is intentionally non-blocking.
        } finally {
          setIsBootstrapping(false);
        }
      };

      void runBootstrap();
    }
  }, [convex, ensureUser, isAuthenticated, organizationAccess]);

  // A present user still without organizations after bootstrap gets the dedicated
  // no-organization page instead of an app shell that can never load.
  useEffect(() => {
    // Don't redirect if already redirected
    if (redirected.current) return;

    // Wait for bootstrap to attempt and complete
    if (!bootstrapAttempted.current || isBootstrapping) return;

    // Wait for query to complete
    if (!isAuthenticated || organizationAccess === undefined) return;
    if (!postBootstrapAccess) return;

    // Only redirect once the user row exists and still has no organizations.
    // Missing user means provisioning failed or is delayed, not confirmed no-org access.
    if (
      postBootstrapAccess.userStatus === "present" &&
      postBootstrapAccess.organizations.length === 0
    ) {
      redirected.current = true;
      navigate({ to: "/error/no-organization" });
    }
  }, [isAuthenticated, isBootstrapping, organizationAccess, postBootstrapAccess, navigate]);

  // Initialize selected org from localStorage once orgs are loaded
  useEffect(() => {
    if (initializedFromStorage.current) return;
    if (!organizations || organizations.length === 0) return;

    initializedFromStorage.current = true;
    const storedId = getStoredOrgId();

    if (storedId) {
      // Validate stored ID exists in user's org list
      const found = organizations.find((org) => org._id === storedId);
      if (found) {
        setSelectedOrgId(found._id);
        setHasInitializedSelection(true);
        return;
      }
      // Stored org no longer valid — clear it
    }

    // Default to first org
    setSelectedOrgId(organizations[0]!._id);
    storeOrgId(organizations[0]!._id);
    setHasInitializedSelection(true);
  }, [organizations]);

  const [organizationSwitchRequest, setOrganizationSwitchRequest] =
    useState<OrganizationSwitchRequest | null>(null);

  const switchOrganization = useCallback(
    (orgId: Id<"orgs">, options?: SwitchOrganizationOptions) => {
      setSelectedOrgId(orgId);
      storeOrgId(orgId);
      setOrganizationSwitchRequest({
        orgId,
        keepCurrentLocation: options?.keepCurrentLocation ?? false,
      });
    },
    []
  );

  // Resolve current organization: explicit selection → first org → null
  const organization = useMemo(
    () =>
      hasInitializedSelection
        ? ((selectedOrgId ? organizations?.find((org) => org._id === selectedOrgId) : null) ??
          organizations?.[0] ??
          null)
        : null,
    [hasInitializedSelection, organizations, selectedOrgId]
  );
  const organizationId = organization?._id ?? null;

  const organizationContextValue = useMemo(
    () => ({
      organizationId,
      organization,
      allOrganizations: organizations,
      switchOrganization,
      organizationSwitchRequest,
      isLoading:
        (organizationAccess === undefined && isAuthenticated) ||
        isBootstrapping ||
        (Boolean(organizations?.length) && !hasInitializedSelection) ||
        (organizationAccess?.userStatus === "missing" && postBootstrapAccess === null) ||
        (organizationAccess?.organizations.length === 0 && postBootstrapAccess === null),
    }),
    [
      isAuthenticated,
      isBootstrapping,
      hasInitializedSelection,
      organization,
      organizationAccess,
      organizationId,
      organizations,
      organizationSwitchRequest,
      postBootstrapAccess,
      switchOrganization,
    ]
  );

  return (
    <OrganizationContext.Provider value={organizationContextValue}>
      {children}
    </OrganizationContext.Provider>
  );
}

export function useOrganization() {
  const context = useContext(OrganizationContext);
  if (!context) {
    throw new Error("useOrganization must be used within OrganizationProvider");
  }
  return context;
}

export function useOptionalOrganization() {
  return useContext(OrganizationContext);
}
